import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';

const SKELETON_DELAY_MS = 220;

export function useDelayed(active: boolean, delay = SKELETON_DELAY_MS): boolean {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (!active) {
      setShown(false);
      return;
    }
    const t = setTimeout(() => setShown(true), delay);
    return () => clearTimeout(t);
  }, [active, delay]);

  return shown;
}

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={cn('rounded-[6px] bg-[var(--line-2)]', className)}
      style={{ animation: 'bb-skeleton 1.8s ease-in-out infinite' }}
    />
  );
}

export function RouteProgress({ active, className }: { active: boolean; className?: string }) {
  const shown = useDelayed(active, 90);

  if (!shown) return null;

  return (
    <div
      role="progressbar"
      aria-label="Loading"
      data-noprint="1"
      className={cn(
        'bb-fade-in pointer-events-none absolute inset-x-0 top-0 z-10 h-0.5 overflow-hidden',
        className,
      )}
    >
      <div
        className="h-full w-full bg-[var(--accent)]"
        style={{ animation: 'bb-progress 1.1s var(--ease) infinite' }}
      />
    </div>
  );
}

export function SkeletonRows({ rows = 8, cols = 5 }: { rows?: number; cols?: number }) {
  const shown = useDelayed(true);

  if (!shown) return <div aria-hidden="true" className="flex flex-col" style={{ opacity: 0 }} />;

  return (
    <div className="bb-fade-in flex flex-col">
      {Array.from({ length: rows }).map((_, r) => (
        <div
          key={r}
          className="bb-row-h flex items-center gap-4 border-b border-[var(--line-2)] px-[var(--pad)]"
        >
          {Array.from({ length: cols }).map((__, c) => (
            <Skeleton key={c} className={cn('h-3', c === 2 ? 'flex-1' : 'w-20')} />
          ))}
        </div>
      ))}
    </div>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-14 text-center',
        className,
      )}
    >
      {icon && <div className="text-[var(--ink-3)]">{icon}</div>}
      <div>
        <p className="m-0 text-[14px] font-medium text-[var(--ink)]">{title}</p>
        {description && (
          <p className="mx-auto mt-1.5 max-w-[52ch] text-[12.5px] text-[var(--ink-2)]">
            {description}
          </p>
        )}
      </div>
      {action}
    </div>
  );
}

export type CalloutTone = 'info' | 'warn' | 'danger' | 'success';

const CALLOUT: Record<CalloutTone, string> = {
  info: 'border-[var(--line)] bg-[var(--surface-2)] text-[var(--ink-2)]',
  warn: 'border-[var(--warn)]/30 bg-[var(--warn-soft)] text-[var(--warn)]',
  danger: 'border-[var(--debit)]/30 bg-[var(--debit-soft)] text-[var(--debit)]',
  success: 'border-[var(--credit)]/30 bg-[var(--accent-soft)] text-[var(--credit)]',
};

export function Callout({
  tone = 'info',
  title,
  children,
  className,
}: {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'rounded-[var(--radius)] border px-3.5 py-2.5 text-[12.5px]',
        CALLOUT[tone],
        className,
      )}
    >
      {title && <div className="font-medium">{title}</div>}
      {children && <div className={cn(title && 'pt-1 opacity-90')}>{children}</div>}
    </div>
  );
}

export function PulseDot({
  tone = 'ok',
  className,
}: {
  tone?: 'ok' | 'warn' | 'bad';
  className?: string;
}) {
  const colour = tone === 'ok' ? 'var(--credit)' : tone === 'warn' ? 'var(--warn)' : 'var(--debit)';
  return (
    <span
      aria-hidden="true"
      className={cn('inline-block size-1.5 rounded-full', className)}
      style={{ background: colour, animation: 'bb-pulse-dot 2.4s ease-in-out infinite' }}
    />
  );
}
